import React, { useState, useEffect } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import AppButton from "../../components/AppButton";
import SynamediaAuthContainer from "../../components/SynamediaAuthContainer";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import cookiesNames from "../../config/cookies";
import { Dropdown, HelpIcon, Tooltip } from '@ip-synamedia/common-ui-library';
import { getDomain } from "../../config";

const useStyles = makeStyles(() => ({
	rootContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		width: 420,
		margin: "0 auto 0 auto"
	},
	formContainer: {
		display: "flex",
		flexDirection: "column",
		alignSelf: "center"

	},
	userInputContainer: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
	},
	selectAccountButton: {
		width: '100%',
		height: 40,
		marginTop: 35,
		marginBottom: 40,
		fontWeight: 400,
		fontFamily: 'Source Sans Pro'
	},
	labelWelcome: {
		height: 84,
		width: '80%',
		fontFamily: 'Poppins',
		fontStyle: 'normal',
		fontWeight: 600,
		fontSize: 28,
		textAlign: 'center',
		color: '#11161D',
	},
	label: {
		fontFamily: 'Source Sans Pro',
		fontStyle: 'normal',
		fontWeight: 400,
		fontSize: 20,
		textAlign: 'center',
		color: '#293447',
	},
	userLabel: {
		fontFamily: 'Source Sans Pro',
		fontStyle: 'normal',
		fontWeight: 400,
		fontSize: 20,
		textAlign: 'center',
		color: '#293447',
		marginTop: '32px'
	},
	selectAccountLabel: {
		fontFamily: 'Source Sans Pro',
		fontStyle: 'normal',
		fontWeight: 400,
		fontSize: 20,
		textAlign: 'center',
		color: '#293447',
		marginTop: '32px',
		marginBottom: '16px'
	},
	selectUserLabel: {
		fontFamily: 'Source Sans Pro',
		fontStyle: 'normal',
		fontWeight: 400,
		fontSize: 20,
		textAlign: 'center',
		color: '#293447',
		marginBottom: '16px',
		marginTop: '32px'
	},
	userBtn:{
		color: '#2966CC',
		fontSize: 14,
		fontFamily: 'Source Sans Pro',
		fontWeight: 400,
		alignSelf: 'center',
		marginBottom: '8px'
	},
	historyBtn:{
		color: '#2966CC',
		fontSize: 14,
		fontFamily: 'Source Sans Pro',
		fontWeight: 400,
		alignSelf: 'center',
		marginTop: '8px'
	},
	link: {
		display: "flex",
		alignItems: "center"
	}
}));

const MultiAccountLoginHome = () => {
	const styles = useStyles();
	const navigate = useNavigate();
	const [cookies, setCookie, removeCookie] = useCookies([cookiesNames.accountAlias, cookiesNames.accounts, cookiesNames.userId]);
	const accountsCookie = cookies?.[cookiesNames.accounts] ?? [];

	const removeCookies = (cookieNames) => {
		cookieNames.forEach(cookieName => {
			removeCookie(cookieName, {
				path: '/',
				...(!window.location.hostname.includes('localhost') && { domain: `.${getDomain()}` })
			});
		});
	};

	// If the last used user id is not defined e.g. user has removed cookie, then we'll default to the first user in the remaining list
	const lastUsedUserId = cookies?.[cookiesNames.userId] ?? accountsCookie?.[0]?.userId;

	// If we still don't have a user then go to loginHome, where the account name entry screen will be seen.
	// This scenario can also occur if the user deletes all users and is returned to the account name entry screen and then
	// presses back or directly uses the multi account URL when there is no user defined.
	useEffect(() => {
		if (!lastUsedUserId) navigate(`/loginHome`);
	}, [lastUsedUserId, navigate]);

	const [selectedUser, setSelectedUser] = useState(lastUsedUserId);
	const userDropdownOptions = accountsCookie.map(entry => ({ id: entry.userId, label: entry.userId }));
	const accountsForSelectedUser = accountsCookie.find(entry => entry.userId === selectedUser)?.accounts ?? [];

	accountsForSelectedUser.sort((a, b) => a.alias.localeCompare(b.alias));
	const accountDropdownOptions = accountsForSelectedUser.map(entry => ({ id : entry.alias, label: entry.alias }));

	// For the account selection dropdown box we default to showing the last logged into account.
	// If there isn't one then default to the first account in the list.
	let lastLoggedIntoAccount = cookies?.[cookiesNames.accountAlias];

	if (!lastLoggedIntoAccount && accountsForSelectedUser?.length > 0) {
		lastLoggedIntoAccount = accountsForSelectedUser[0].alias;
	}

	const [selectedAccount, setSelectedAccount] = useState(lastLoggedIntoAccount);
	const handleAccountSelectionChange = (value) => setSelectedAccount(value);
	const handleUseDifferentUserClick = () => navigate(`/loginHome?enterAccountName=true`);
	const handleLoginClick = () => navigate(`/${selectedAccount}/home?email=${selectedUser}`);

	const handleUserChanged = (value) => {
		setSelectedUser(value);

		// If the selected user is changed then we need to update the default selected account.
		// If the last logged into account does not exist for the newly selected user then we default to the first
		// account in the list.
		const accountsForNewUser = accountsCookie.find(entry => entry.userId === value)?.accounts ?? [];

		setSelectedAccount(accountsForNewUser.find(entry => entry.alias === lastLoggedIntoAccount) ?
			lastLoggedIntoAccount : accountsForNewUser[0].alias);
	};

	const handleRemoveUserClick = () => {
		const userIndex = accountsCookie.findIndex(entry => entry.userId === selectedUser);

		if (userIndex !== -1) {
			// remove this user and all of their accounts from the cookie
			const updatedAccountsCookie = accountsCookie.filter((_, index) => index !== userIndex);

			setCookie(cookiesNames.accounts, updatedAccountsCookie, {
				path: '/',
				maxAge: 86400 * 365 * 10,
				...(!window.location.hostname.includes('localhost') && { domain: `.${getDomain()}` })
			});

			// We've removed the currently selected user, so default to the first in the list.
			if (updatedAccountsCookie.length > 0) setSelectedUser(updatedAccountsCookie[0].userId);

			// If the last used user for sign in was removed and there are more users, default to the first user.
			if (lastUsedUserId === selectedUser && updatedAccountsCookie.length > 0) {
				setCookie(cookiesNames.userId, updatedAccountsCookie[0].userId, {
					path: '/',
					maxAge: 86400 * 365 * 10,
					...(!window.location.hostname.includes('localhost') && { domain: `.${getDomain()}` })
				});
			}

			// User has removed all their email accounts from their list so remove all history and go to login
			// page where they will see the 'enter account name' screen.
			if (updatedAccountsCookie.length === 0)  {
				removeCookies([
					cookiesNames.accountAlias,
					cookiesNames.accountOrgId,
					cookiesNames.accounts,
					cookiesNames.userId
				]);
				navigate(`/loginHome`);
			}
		}
	};

	const content = (
		<div className={styles.rootContainer}>
			<div className={styles.labelWelcome} id={'LoginHome_labelWelcome'}>Welcome to the account-portal</div>
			{accountsCookie.length > 1 ?
				<>
					<div className={styles.selectUserLabel} id={'LoginHome_label'}>We have identified that the following user is a member of multiple accounts</div>
					<div className={styles.formContainer}>
						<div className={styles.userInputContainer}>
							<Dropdown options={userDropdownOptions} value={selectedUser} onValueChange={handleUserChanged} width="100%"/>
						</div>
					</div>
				</> :
				<div className={styles.selectUserLabel} id={'LoginHome_label'}>We have identified that the user {selectedUser} is a member of multiple accounts</div>
			}
			<div className={styles.link}>
				<AppButton
					variant={'text'}
					overrideClasses={{ button: styles.historyBtn }}
					id={'btn_remove_user'}
					onClick={handleRemoveUserClick}
				>
					Remove this user from your history
				</AppButton>
				<div style={{ paddingTop: 16 }}>
					<Tooltip content={"If you no longer wish to display accounts associated with this email address, then click here to remove this email address from your history."}>
						<HelpIcon/>
					</Tooltip>
				</div>
			</div>
			<div className={styles.link}>
				<AppButton
					variant={'text'}
					overrideClasses={{ button: styles.userBtn }}
					id={'btn_different_user'}
					onClick={handleUseDifferentUserClick}
				>
				Sign in using a different user
				</AppButton>
				<Tooltip content={"If you have account(s) associated with another email address not shown here, click here to sign into that account using that email address."}>
					<HelpIcon/>
				</Tooltip>
			</div>
			<div className={styles.selectAccountLabel} id={'LoginHome_label'}>Please select an account to continue</div>
			<div className={styles.formContainer}>
				<div className={styles.userInputContainer}>
					<Dropdown options={accountDropdownOptions} value={selectedAccount} onValueChange={handleAccountSelectionChange} width="100%"/>
				</div>
				<AppButton
					variant={'contained'}
					type={'submit'}
					overrideClasses={{ button: styles.selectAccountButton }}
					id={'btn_login'}
					onClick={handleLoginClick}
				>
					Sign In
				</AppButton>
			</div>
		</div>
	);

	document.title = 'Synamedia Cloud Portal | Login';

	return (
		<SynamediaAuthContainer
			content={content}
		/>
	);
};

export default MultiAccountLoginHome;
